/**
 * Per-locale metadata used by the i18n layer:
 * - `dir`: text direction (`ltr` for most languages, `rtl` for Arabic/Hebrew/Persian/Urdu)
 * - `nativeName`: native autonym, used as a fallback for the locale switcher when
 *   a tenant doesn't supply `localeNames` in site.config.ts.
 * - `numberFormatLocale`: the BCP-47 tag passed to Intl.NumberFormat /
 *   Intl.DateTimeFormat. Usually identical to the locale code, but bare codes
 *   like "es" can be paired with a region (e.g., "es-MX") for sensible numeric
 *   defaults.
 *
 * RTL plumbing is dormant — no tenant uses Arabic/Hebrew today — but the
 * lookup is here so enabling an RTL locale is a config flip, not a refactor.
 */

export type LocaleDir = 'ltr' | 'rtl';

export interface LocaleMeta {
  dir: LocaleDir;
  nativeName: string;
  numberFormatLocale: string;
}

const META: Record<string, LocaleMeta> = {
  en: { dir: 'ltr', nativeName: 'English', numberFormatLocale: 'en-US' },
  es: { dir: 'ltr', nativeName: 'Español', numberFormatLocale: 'es-MX' },
  fr: { dir: 'ltr', nativeName: 'Français', numberFormatLocale: 'fr-FR' },
  de: { dir: 'ltr', nativeName: 'Deutsch', numberFormatLocale: 'de-DE' },
  pt: { dir: 'ltr', nativeName: 'Português', numberFormatLocale: 'pt-BR' },
  it: { dir: 'ltr', nativeName: 'Italiano', numberFormatLocale: 'it-IT' },
  nl: { dir: 'ltr', nativeName: 'Nederlands', numberFormatLocale: 'nl-NL' },
  ar: { dir: 'rtl', nativeName: 'العربية', numberFormatLocale: 'ar-SA' },
  he: { dir: 'rtl', nativeName: 'עברית', numberFormatLocale: 'he-IL' },
  fa: { dir: 'rtl', nativeName: 'فارسی', numberFormatLocale: 'fa-IR' },
  ur: { dir: 'rtl', nativeName: 'اردو', numberFormatLocale: 'ur-PK' },
  zh: { dir: 'ltr', nativeName: '中文', numberFormatLocale: 'zh-CN' },
  ja: { dir: 'ltr', nativeName: '日本語', numberFormatLocale: 'ja-JP' },
  ko: { dir: 'ltr', nativeName: '한국어', numberFormatLocale: 'ko-KR' },
};

/**
 * Returns metadata for a locale code. Strips the region (e.g., "es-MX" → "es")
 * when looking up bare codes, so tenants who declare a region-tagged locale
 * still get sensible defaults. The original (region-tagged) code is kept as
 * the formatting locale so currency/date display matches the tenant's intent.
 */
export function getLocaleMeta(locale: string): LocaleMeta {
  const direct = META[locale];
  if (direct) return direct;
  const base = locale.split('-')[0]?.toLowerCase();
  if (base && META[base]) {
    return { ...META[base], numberFormatLocale: locale };
  }
  return { dir: 'ltr', nativeName: locale, numberFormatLocale: locale };
}
